import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ButtonComponent';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Modal } from '@/feature/alerts/components/Modal';
import { BetslipModalHeader } from '@/feature/betslip-sbk/components/BetslipModalHeader';
import { betslipModalStyles } from '@/feature/betslip-sbk/components/shared-modal-styles';
import { SelectionParam } from '@/feature/betslip-sbk/types';
import { formatEventHeader } from '@/feature/betslip-sbk/utils/formatter';
import { useMarketName } from '@/feature/event-details-sbk/hooks/use-market-name';
import { useOptionName } from '@/feature/event-details-sbk/hooks/use-option-name';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { BottomSheetFlatList, BottomSheetFooter, BottomSheetFooterProps, BottomSheetModal } from '@gorhom/bottom-sheet';

export type PicksNotAddedModalRef = {
    show: (selections: SelectionParam[]) => void;
    dismiss: () => void;
};

const MODAL_ID = 'picksNotAddedModal';

const styles = StyleSheet.create({
    marketLineSeparator: {
        height: 16,
    },
});

const PicksNotAddedItem = ({ selection }: { selection: SelectionParam }) => {
    const { option, market, event } = selection;

    const optionName = useOptionName({
        optionType: option.optionType,
        marketType: market.marketType,
        homeTeam: event.home_team,
        awayTeam: event.away_team,
        fallback: option.description,
        sportName: event.sport.name,
    });

    const marketName = useMarketName({
        sportName: event.sport.name,
        marketType: market.marketType,
        event,
        player: market.player,
        appendPlayerName: true,
        appendTeamName: !market.isMicroMarket,
        appendMicroMarketDescription: market.isMicroMarket,
        isExtendedName: true,
        fallback: market.description,
    });

    const eventHeader = formatEventHeader(event);

    return (
        <Box paddingVertical="s16" borderBottomWidth={1} borderBottomColor={ds.colorToken.border.strong} height={80}>
            <Text variant="bodySmall" color="neutral300">
                {eventHeader}
            </Text>
            <Row gap="s8" mt="s4" alignItems="center">
                <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
                    {optionName}
                </Text>
                <LineSeparator vertical style={styles.marketLineSeparator} />
                <Text variant="bodyMedium" color="neutral300">
                    {marketName}
                </Text>
            </Row>
        </Box>
    );
};

const keyExtractor = (selection: SelectionParam) => selection.option.id;

export const PicksNotAddedModal = React.forwardRef<PicksNotAddedModalRef, object>((_props, ref) => {
    const modalRef = useRef<BottomSheetModal>(null);
    const { t } = useTranslation(['betslip_sbk', 'common']);
    const { bottom } = useSafeAreaInsets();

    const [selections, setSelections] = useState<SelectionParam[]>([]);

    useImperativeHandle(ref, () => ({
        show: (data: SelectionParam[]) => {
            setSelections(data);
            modalRef.current?.present();
        },
        dismiss: () => {
            modalRef.current?.dismiss();
        },
    }));

    const renderItem = useCallback(({ item }: { item: SelectionParam }) => <PicksNotAddedItem selection={item} />, []);

    const handleGotIt = useCallback(() => {
        modalRef.current?.dismiss();
    }, []);

    const footerStyle = useMemo(() => ({ paddingBottom: bottom + 16 || 16 }), [bottom]);

    const renderListHeader = useCallback(() => <BetslipModalHeader title={t('picks_not_added')} />, [t]);

    const footerComponent = useCallback(
        (props: BottomSheetFooterProps) => (
            <BottomSheetFooter {...props} style={betslipModalStyles.contentContainer}>
                <Box px="s16" pt="s16" style={footerStyle}>
                    <Button label={t('common:got_it')} hierarchy="primary" onPress={handleGotIt} />
                </Box>
            </BottomSheetFooter>
        ),
        [t, handleGotIt, footerStyle]
    );

    return (
        <Modal id={MODAL_ID} modalRef={modalRef} hasBottomSheetView={false} footerComponent={footerComponent}>
            <BottomSheetFlatList
                data={selections}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                ListHeaderComponent={renderListHeader}
                contentContainerStyle={betslipModalStyles.listContentLargerPadding}
                showsVerticalScrollIndicator={showScrollIndicator}
                enableFooterMarginAdjustment={true}
            />
        </Modal>
    );
});
